type IRawHistoryPoint = {
  x: unknown;
  y: unknown;
};

export type IChartHistoryPoint = {
  x: Date;
  y: number;
};

export interface IChartSeriesData {
  suffix: string;
  histories: IChartHistoryPoint[];
  allZeros?: boolean;
}

const CHART_MEMORY_CACHE_MAX_KEYS = 64;
const chartSeriesMemoryStore = new Map<string, IChartSeriesData>();

function toSafeNumber(raw: unknown): number | undefined {
  if (typeof raw != "number" || !Number.isFinite(raw)) return undefined;
  return raw;
}

function toTimestampMs(raw: unknown): number | undefined {
  if (raw instanceof Date) {
    const value = raw.getTime();
    return Number.isFinite(value) ? value : undefined;
  }

  const numeric = toSafeNumber(raw);
  if (numeric == undefined) return undefined;
  return numeric;
}

function normalizePoint(raw: IRawHistoryPoint): IChartHistoryPoint | undefined {
  const timestampMs = toTimestampMs(raw.x);
  if (timestampMs == undefined || timestampMs < 0) return undefined;

  const yRaw = toSafeNumber(raw.y);
  const y = yRaw == undefined ? 0 : yRaw;

  return {
    x: new Date(timestampMs),
    y,
  };
}

function toSafeIntervalSeconds(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  return Math.floor(raw);
}

function toSafePointLimit(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  return Math.floor(raw);
}

function alignDown(timestampSec: number, stepSec: number): number {
  return Math.floor(timestampSec / stepSec) * stepSec;
}

const HASHRATE_SUFFIX_POW = new Map<string, number>([
  ["H/S", 0],
  ["KH/S", 3],
  ["MH/S", 6],
  ["GH/S", 9],
  ["TH/S", 12],
  ["PH/S", 15],
  ["EH/S", 18],
  ["ZH/S", 21],
  ["YH/S", 24],
]);

function resolveHashrateSuffixPow(suffix: string | undefined): number | undefined {
  if (typeof suffix != "string") return undefined;
  const normalized = suffix.trim().toUpperCase();
  if (normalized.length == 0) return undefined;
  return HASHRATE_SUFFIX_POW.get(normalized);
}

function cloneChartSeries(
  series: IChartSeriesData,
  maxPoints: number
): IChartSeriesData {
  return {
    suffix: series.suffix,
    histories: normalizeHistoryPoints(series.histories, maxPoints),
    allZeros: series.allZeros,
  };
}

function enforceMemoryCacheLimit(): void {
  while (chartSeriesMemoryStore.size > CHART_MEMORY_CACHE_MAX_KEYS) {
    const oldestKey = chartSeriesMemoryStore.keys().next().value;
    if (!oldestKey) return;
    chartSeriesMemoryStore.delete(oldestKey);
  }
}

export function normalizeHistoryPoints(
  histories: Array<{ x: unknown; y: unknown }> | undefined,
  maxPoints: number
): IChartHistoryPoint[] {
  const safeMaxPoints = toSafePointLimit(maxPoints);
  const normalized = (histories ?? [])
    .map((raw) => normalizePoint(raw))
    .filter((point): point is IChartHistoryPoint => point != undefined)
    .sort((a, b) => a.x.getTime() - b.x.getTime());

  if (normalized.length <= safeMaxPoints) return normalized;
  return normalized.slice(-safeMaxPoints);
}

export function isHistoryAllZero(
  histories: Array<{ x: unknown; y: unknown }> | undefined
): boolean {
  return (histories ?? []).every((point) => {
    const y = Number(point.y);
    return !Number.isFinite(y) || y == 0;
  });
}

interface IMergeHistoryByIntervalArgs {
  previous: Array<{ x: unknown; y: unknown }> | undefined;
  incoming: Array<{ x: unknown; y: unknown }> | undefined;
  groupByIntervalSec: number;
  maxPoints: number;
}

export function mergeHistoryByInterval({
  previous,
  incoming,
  groupByIntervalSec,
  maxPoints,
}: IMergeHistoryByIntervalArgs): IChartHistoryPoint[] {
  const safeInterval = toSafeIntervalSeconds(groupByIntervalSec);
  const safeMaxPoints = toSafePointLimit(maxPoints);
  const mergedByBucket = new Map<number, number>();

  const append = (source: Array<{ x: unknown; y: unknown }> | undefined) => {
    for (const point of source ?? []) {
      const timestampMs = toTimestampMs(point.x);
      if (timestampMs == undefined || timestampMs < 0) continue;

      const bucketSec = alignDown(Math.floor(timestampMs / 1000), safeInterval);
      const y = Number(point.y);
      mergedByBucket.set(bucketSec, Number.isFinite(y) ? y : 0);
    }
  };

  append(previous);
  append(incoming);

  const ordered = Array.from(mergedByBucket.entries())
    .sort((first, second) => first[0] - second[0])
    .slice(-safeMaxPoints)
    .map(([bucketSec, y]) => ({
      x: new Date(bucketSec * 1000),
      y,
    }));

  return ordered;
}

interface IFilterHistoryFromTimeArgs {
  histories: Array<{ x: unknown; y: unknown }> | undefined;
  timeFromSec: number;
  maxPoints: number;
}

export function filterHistoryFromTime({
  histories,
  timeFromSec,
  maxPoints,
}: IFilterHistoryFromTimeArgs): IChartHistoryPoint[] {
  if (!Number.isFinite(timeFromSec) || timeFromSec <= 0) {
    return normalizeHistoryPoints(histories, maxPoints);
  }

  const thresholdMs = Math.floor(timeFromSec) * 1000;
  return normalizeHistoryPoints(histories, maxPoints).filter(
    (point) => point.x.getTime() >= thresholdMs
  );
}

interface IConvertHistorySuffixArgs {
  histories: Array<{ x: unknown; y: unknown }> | undefined;
  fromSuffix: string | undefined;
  toSuffix: string | undefined;
  maxPoints: number;
}

export function convertHistorySuffix({
  histories,
  fromSuffix,
  toSuffix,
  maxPoints,
}: IConvertHistorySuffixArgs): IChartHistoryPoint[] {
  const normalized = normalizeHistoryPoints(histories, maxPoints);
  if (normalized.length == 0) return normalized;

  const fromPow = resolveHashrateSuffixPow(fromSuffix);
  const toPow = resolveHashrateSuffixPow(toSuffix);
  if (fromPow == undefined || toPow == undefined || fromPow == toPow) {
    return normalized;
  }

  const multiplier = Math.pow(10, fromPow - toPow);
  if (!Number.isFinite(multiplier) || multiplier <= 0) return normalized;

  return normalized.map((point) => ({
    x: new Date(point.x),
    y: point.y * multiplier,
  }));
}

interface IResolveIncrementalTimeFromArgs {
  previous: Array<{ x: unknown; y: unknown }> | undefined;
  groupByIntervalSec: number;
  maxIntervals: number;
  nowTimestampSec?: number;
}

export function resolveIncrementalTimeFrom({
  previous,
  groupByIntervalSec,
  maxIntervals,
  nowTimestampSec,
}: IResolveIncrementalTimeFromArgs): number | undefined {
  const safeInterval = toSafeIntervalSeconds(groupByIntervalSec);
  const safeMaxIntervals = toSafePointLimit(maxIntervals);
  const nowSec =
    nowTimestampSec != undefined && Number.isFinite(nowTimestampSec)
      ? Math.floor(nowTimestampSec)
      : Math.floor(Date.now() / 1000);
  const uniqueBucketsDesc = Array.from(
    new Set(
      normalizeHistoryPoints(previous, safeMaxIntervals + 8)
        .map((point) => Math.floor(point.x.getTime() / 1000))
        .filter((timestampSec) => Number.isFinite(timestampSec) && timestampSec > 0)
        .map((timestampSec) => alignDown(timestampSec, safeInterval))
    )
  ).sort((a, b) => b - a);

  if (uniqueBucketsDesc.length < 2) return undefined;

  const secondNewestBucket = uniqueBucketsDesc[1];
  if (!Number.isFinite(secondNewestBucket) || secondNewestBucket <= 0) {
    return undefined;
  }

  const elapsed = nowSec - secondNewestBucket;
  if (elapsed > safeInterval * safeMaxIntervals) return undefined;

  const withOverlap = secondNewestBucket - safeInterval;
  if (!Number.isFinite(withOverlap) || withOverlap <= 0) return undefined;
  return withOverlap;
}

export function readChartSeriesFromMemory(
  key: string,
  maxPoints: number
): IChartSeriesData | undefined {
  if (!key) return undefined;
  const found = chartSeriesMemoryStore.get(key);
  if (found == undefined) return undefined;

  const cloned = cloneChartSeries(found, maxPoints);
  chartSeriesMemoryStore.set(key, cloned);
  return cloned;
}

export function writeChartSeriesToMemory(
  key: string,
  series: IChartSeriesData | undefined,
  maxPoints: number
): void {
  if (!key) return;

  if (series == undefined) {
    chartSeriesMemoryStore.delete(key);
    return;
  }

  chartSeriesMemoryStore.set(key, cloneChartSeries(series, maxPoints));
  enforceMemoryCacheLimit();
}
